/**
 * API client for the separate NestJS service.
 *
 * Everything goes out with `credentials: 'include'` because the session is an
 * httpOnly cookie scoped to `.pms.cargofl.com`, shared between pms.cargofl.com and
 * api.pms.cargofl.com. No token is ever held in JS, so there is nothing here for an
 * XSS to read - and nothing to attach to requests by hand.
 */

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code from the API, e.g. 'STAGE_NOT_ACTIVE'. */
  readonly code: string | null;
  /**
   * Per-field messages from ZodValidationPipe. Carried on the error rather than
   * flattened into `message`: the forms attach each one to its own input, and
   * "Some fields need attention" alone names no field the user can go and fix.
   */
  readonly fieldErrors: Record<string, string[]> | null;

  constructor(
    status: number,
    message: string,
    code: string | null = null,
    fieldErrors: Record<string, string[]> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }

  /** No session, or it expired/was revoked - the shell should show the login screen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * The employee record exists but is EXITED. Distinct from unauthenticated: their
   * credentials are fine, so bouncing them to the login screen would just loop.
   */
  get isAccountDisabled(): boolean {
    return this.status === 403 && this.code === 'ACCOUNT_DISABLED';
  }
}

interface ApiErrorBody {
  message?: string;
  code?: string;
  fieldErrors?: Record<string, string[]>;
}

/**
 * Retried only for reads.
 *
 * A GET can be repeated safely; a POST that submits an assessment stage cannot -
 * the request may have succeeded and only the response been lost, and a blind
 * retry would submit twice. The API's own concurrency guards would reject the
 * duplicate, but the honest place to not retry is here.
 */
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const RETRY_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = 300;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether the API answered the last request at all, for ServerDownOverlay.
 *
 * Only a network failure, 502 or 504 flips it: those come from the browser or nginx and
 * mean the API itself is down. A 503 does not — the API sends that for one dependency
 * (attachment storage, say), and blanking the whole app over one failing upload is wrong.
 * Any other response, error or not, proves the API is up.
 */
const UNREACHABLE_STATUS = new Set([502, 504]);
let apiReachable = true;
const reachabilityListeners = new Set<() => void>();

function setApiReachable(value: boolean): void {
  if (value === apiReachable) return;
  apiReachable = value;
  reachabilityListeners.forEach((listener) => listener());
}

export const apiReachability = {
  get: (): boolean => apiReachable,
  subscribe(listener: () => void): () => void {
    reachabilityListeners.add(listener);
    return () => reachabilityListeners.delete(listener);
  },
};

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  // FormData sets its own Content-Type, and it has to: the multipart boundary is
  // generated per request, so declaring the type by hand produces a body the
  // server cannot split back into fields.
  const isMultipart = init.body instanceof FormData;
  const hasJsonBody = init.body !== undefined && !isMultipart;

  const method = (init.method ?? 'GET').toUpperCase();
  const canRetry = method === 'GET';

  let response!: Response;
  for (let attempt = 0; ; attempt += 1) {
    const last = attempt === RETRY_ATTEMPTS - 1;
    try {
      response = await fetch(`${API_URL}${path}`, {
        ...init,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
          ...init.headers,
        },
      });
    } catch (error) {
      // fetch only rejects on a network-level failure - a dropped wifi link, a
      // restarting API behind nginx. Worth one more try before telling someone
      // their dashboard is broken.
      if (!canRetry || last) {
        setApiReachable(false);
        throw error;
      }
      await delay(RETRY_BACKOFF_MS * 2 ** attempt);
      continue;
    }

    // 502/503/504 is nginx or the API mid-restart, not an answer about the data.
    if (canRetry && !last && RETRYABLE_STATUS.has(response.status)) {
      await delay(RETRY_BACKOFF_MS * 2 ** attempt);
      continue;
    }
    break;
  }

  setApiReachable(!UNREACHABLE_STATUS.has(response.status));
  if (response.status === 204) return undefined as T;

  // An error page from nginx (502, say) is HTML, not JSON - don't let the parse
  // failure mask the real status code the caller needs to react to.
  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    const body = (payload ?? {}) as ApiErrorBody;
    throw new ApiError(
      response.status,
      body.message ?? `Request failed with ${response.status}`,
      body.code ?? null,
      body.fieldErrors ?? null,
    );
  }

  return payload as T;
}

/** The name the server put in Content-Disposition, or null if it sent none. */
function filenameFrom(disposition: string | null): string | null {
  const match = /filename="?([^";]+)"?/i.exec(disposition ?? '');
  return match?.[1] ?? null;
}

/**
 * Download a file the API generates, with the session cookie attached.
 *
 * Not a plain `<a href>` to the API. The session lives on `.pms.cargofl.com` and the
 * API is a different origin, so a link would either drop the cookie or navigate away
 * from the app on an error - and a 403 would render as a bare JSON page instead of a
 * toast. Fetching it means an export that fails, fails the way everything else does.
 *
 * Deliberately not `apiFetch`: that one parses JSON, and a CSV body would come back as
 * null through it.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { Accept: 'text/csv, application/json' },
  });

  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    const body = (isJson ? await response.json() : {}) as ApiErrorBody;
    throw new ApiError(
      response.status,
      body.message ?? `Export failed with ${response.status}`,
      body.code ?? null,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filenameFrom(response.headers.get('content-disposition')) ?? fallbackName;
  link.click();
  // Revoked on the next tick, not inline: Firefox and Safari read the blob
  // asynchronously after click(), and tearing the URL down synchronously cancels the
  // download. Same reason as downloadCsv in lib/csv.ts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  // PUT rather than PATCH where the request replaces a whole set - the rating
  // distribution is validated as six bands totalling 100, so a partial update of it
  // is not a meaningful request.
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, form: FormData) => apiFetch<T>(path, { method: 'POST', body: form }),
};
